import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTenant } from '@core/tenant';
import { captureApiError } from '@core/sentry';
import { useBooking } from '@features/booking/state';
import type { Reservation } from '../hooks/useUserReservations';
import { checkModifyTimePolicy, type ModifyTimePolicyResponse } from '../api/reservationApi';

type Step = 'loading' | 'cantModify';

interface ChangeDateViewProps {
  reservation: Reservation;
  onClose: () => void;
}

const ChangeDateView = ({ reservation, onClose }: ChangeDateViewProps) => {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const { restaurant } = useTenant();
  const booking = useBooking();

  const [step, setStep] = useState<Step>('loading');
  const [policyHours, setPolicyHours] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      let policy: ModifyTimePolicyResponse | null = null;
      try {
        const response = await checkModifyTimePolicy(reservation.id);
        if (typeof response?.canModify !== 'boolean') {
          throw new Error('Reschedule policy response has no canModify flag');
        }
        policy = response;
      } catch (error) {
        if (cancelled) return;
        // Fail open: a policy we could not read does not block the guest.
        captureApiError(error, { source: 'reschedule-policy-check', reservationId: reservation.id });
      }
      if (cancelled) return;

      if (policy && !policy.canModify) {
        setPolicyHours(policy.cancellationPolicyHours);
        setStep('cantModify');
        return;
      }
      startReschedule();
    }

    function startReschedule() {
      booking.resetBooking();
      booking.updateBooking({
        diners: reservation.partySize,
        rescheduleId: reservation.id,
        rescheduleOriginalType: reservation.type ?? null,
        rescheduleMenuId: reservation.menuId ?? null,
        service: (reservation.serviceType as import('@features/booking/state/types').ServiceType) ?? null,
      });
      navigate('/select-date');
    }

    init().catch((error) => {
      if (cancelled) return;
      // Report once and hand control back; never retry the start.
      captureApiError(error, { source: 'reschedule-start', reservationId: reservation.id });
      onClose();
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation.id]);

  if (step === 'loading') {
    return (
      <div className="w-full flex flex-col items-center py-12">
        <Loader2 size={24} className="animate-spin text-brand" />
      </div>
    );
  }

  // step === 'cantModify'
  return (
    <div className="w-full flex flex-col items-center">
      <h2 className="text-3xl font-display mb-4">{t('MODIFY_RESERVATION_STRING_42_TITLE')}</h2>
      {/* Only a positive hour count is a window; -1, 0 or a missing value means no changes at all. */}
      {policyHours > 0 ? (
        <>
          <p className="text-sm text-foreground/70 text-center mb-2 max-w-xs">
            {t('MODIFY_RESERVATION_STRING_42', { hours: policyHours })}
          </p>
          <p className="text-lg font-semibold text-red-500 mb-6">
            {t('MODIFY_RESERVATION_STRING_43')}
          </p>
        </>
      ) : (
        <p className="text-sm text-foreground/70 text-center mb-6 max-w-xs">
          {t('MODIFY_RESERVATION_STRING_42_CLOSED')}
        </p>
      )}

      {restaurant?.contactEmail && (
        <p className="text-sm text-foreground/50 mb-6">{restaurant.contactEmail}</p>
      )}

      <div className="w-full max-w-xs">
        <button
          className="w-full py-3 rounded-full border border-border text-brand-dark font-medium text-sm tracking-wide hover:bg-foreground/5 transition-colors"
          onClick={onClose}
        >
          {t('MODIFY_RESERVATION_STRING_42_GO_BACK')}
        </button>
      </div>
    </div>
  );
};

export default ChangeDateView;
